import { Button, Collapse, Form, Layout, Space, Spin, message } from "antd";
import {
  STORE_REQUISITION_LIST,
  STORE_REQUISITION_UPDATE,
} from "apiServices/API_ENDPOINTS";
import { getData, postData } from "apiServices/common";
import HandleNotification from "common/Notification";
import AppPageHeader from "common/PageHeader";
import InventoryLayOutCommon from "components/InventoryFabric/LayOut/InventoryLayOutCommon";
import { StoreRequisitionContext } from "context/StoreRequisitionContext/StoreRequisitionContextProvider";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { STORE_REQUISITION_EDIT_PERMISSION } from "routes/permissions";
import { hasPermission } from "utils/functions";
import StoreRequisitionForm from "../StoreRequisitionForm";
import { buildStoreRequisitionPayload } from "../utils";

const { Content } = Layout;
const { Panel } = Collapse;

const StoreRequisitionUpdate = () => {
  const { id } = useParams();
  const { styleDetails, loadStyleDetailsFromApi, resetStyleDetails, getStyleListByBuyerOrFactory } =
    useContext(StoreRequisitionContext);
  const [isSubmit, setIsSubmit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const fetchDetails = async () => {
      setLoading(true);
      const response = await getData(`${STORE_REQUISITION_LIST}/${id}`);
      if (!isMounted) return;

      if (response?.data?.code === 200) {
        const data = response.data.data;
        form.setFieldsValue({
          buyer_id: data.buyer_id,
          sub_contract_factory_id: data.sub_contract_factory_id,
          entry_date: data.entry_date ? moment(data.entry_date) : null,
          requisition_no: data.requisition_no,
          movement_type: data.movement_type,
          sr_type: data.sr_type,
          status: data.status,
          note: data.note,
        });
        const styles = await getStyleListByBuyerOrFactory({
          buyer_id: data.buyer_id || null,
          sub_contract_factory_id: data.sub_contract_factory_id || null,
        });
        if (isMounted) {
          loadStyleDetailsFromApi(data.sr_style_details || [], styles);
        }
      } else {
        message.error("Failed to load store requisition");
      }
      if (isMounted) {
        setLoading(false);
      }
    };

    fetchDetails();

    return () => {
      isMounted = false;
    };
  }, [id, form, getStyleListByBuyerOrFactory, loadStyleDetailsFromApi]);

  useEffect(() => () => resetStyleDetails(), [resetStyleDetails]);

  const goBack = () => {
    resetStyleDetails();
    navigate("/production/store-requisition");
  };

  const extra = (
    <Space>
      <Button size="small" onClick={goBack}>
        Back
      </Button>
      <Button
        htmlType="submit"
        type="primary"
        size="small"
        onClick={() => form.submit()}
        disabled={!hasPermission([STORE_REQUISITION_EDIT_PERMISSION]) || isSubmit}
        loading={isSubmit}
      >
        {hasPermission([STORE_REQUISITION_EDIT_PERMISSION])
          ? "Update"
          : "No Permission"}
      </Button>
    </Space>
  );

  const onFinish = async (values) => {
    if (!styleDetails.length) {
      message.error("Please add at least one style with items");
      return;
    }

    const hasItems = styleDetails.every(
      (style) => style.items && style.items.length > 0,
    );
    if (!hasItems) {
      message.error("Each style must have at least one item");
      return;
    }

    setIsSubmit(true);
    const bodyData = buildStoreRequisitionPayload(values, styleDetails);
    const response = await postData(`${STORE_REQUISITION_UPDATE}/${id}`, bodyData);

    if (response) {
      HandleNotification(
        "success",
        "bottomRight",
        "Store Requisition updated successfully",
        null,
      );
      resetStyleDetails();
      navigate("/production/store-requisition");
    } else {
      HandleNotification("error", "bottomRight", "Something went wrong", null);
      setIsSubmit(false);
    }
  };

  return (
    <InventoryLayOutCommon>
      <AppPageHeader title="Store Requisition - Edit" extra={extra} />
      <Content className="item-details">
        <Spin spinning={loading}>
          <Collapse defaultActiveKey={["1"]} style={{ marginTop: 15 }}>
            <Panel header={<b>Details</b>} key="1">
              <Form layout="vertical" onFinish={onFinish} form={form}>
                <StoreRequisitionForm form={form} />
              </Form>
            </Panel>
          </Collapse>
        </Spin>
      </Content>
    </InventoryLayOutCommon>
  );
};

export default StoreRequisitionUpdate;
